import dayjs from 'dayjs';
import {themesData} from 'data/themes-data';


/**
 * Get unique facilitators
 * @param {array} allGames 
 * @returns 
 */
const getUniqueFacilitators = (allGames) => {
	const facilitators = [];
	allGames.forEach((game) => {
		const facilitatorIndex = facilitators.findIndex((f) => {return f.id === game.facilitatorId;});
		if (facilitatorIndex < 0) {
			facilitators.push({id: game.facilitatorId, created: game.created});
		} else {
			if (game.created < facilitators[facilitatorIndex].created) {
				facilitators[facilitatorIndex].created = game.created;
			}
		}
	});
	return facilitators;
};


/**
 * Filter data by date
 * @param {object} startDate 
 * @param {object} endDate 
 * @param {array} allGames 
 * @param {array} facilitators 
 * @param {array} allGroups 
 * @returns 
 */
const filterFacilitatorsGamesAndGroupsByDate = (branchId, startDate, endDate, allGames, facilitators, allGroups) => {
	const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
	const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');

	/* Facilitators (date only) */
	const filteredFacilitators = facilitators.filter((f) => {
		return (
			f.hasOwnProperty('created') && 
			f.created >= formattedStartDate &&
			f.created <= formattedEndDate
		);
	});

	/* Games and groups (date and branch) */
	const filteredGames = allGames.filter((g) => {
		return (
			g.branchId === branchId && 
			g.hasOwnProperty('created') && 
			g.created >= formattedStartDate &&
			g.created <= formattedEndDate
		);
	});
	const filteredGroups = allGroups.filter((group) => {
		return (
			filteredGames.some((g) => {return g.id === group.gameId;}) &&
			group.hasOwnProperty('created') && 
			group.created >= formattedStartDate &&
			group.created <= formattedEndDate
		);
	});

	return {filteredGames, filteredFacilitators, filteredGroups};
};


/**
 * Get general stats
 * @param {array} filteredGames 
 * @param {array} filteredGroups 
 * @returns 
 */
const getGeneralStats = (filteredGames, filteredGroups) => {
	const totalStarted = filteredGames.length;
	const totalCompleted = filteredGames.filter((g) => {
		return (
			g.players &&
			g.players.length > 1 &&
			g.players.filter((p) => {
				return p.currentGameStepId && p.currentGameStepId.includes('survey');
			}).length > 1 &&
			g.surveyAnswers &&
			g.surveyAnswers.length > 3 
		);
	}).length;
	const totalCompletedPercent = (totalStarted > 0
		? Math.round(((totalCompleted / totalStarted) * 100.))
		: 0
	);

	/* Get group stats */
	const totalGroups = filteredGroups.length;
	const completedGroups = filteredGroups.filter((g) => {
		return (
			g.currentGameStepId && g.currentGameStepId.includes('survey')
		);
	}).length;
	const completedGroupsPercent = (totalGroups > 0
		? Math.round(((completedGroups / totalGroups) * 100.))
		: 0
	);

	/* Get survey stats */
	let totalSurveyAnswers = 0;
	filteredGames.forEach((game) => {
		if (game.surveyAnswers) {
			totalSurveyAnswers += game.surveyAnswers.length;
		}
	});

	return {
		totalStarted, 
		totalCompleted, 
		totalCompletedPercent, 
		totalGroups, 
		completedGroups, 
		completedGroupsPercent, 
		totalSurveyAnswers
	};
};



/**
 * Get survey result for all games (of specific theme)
 * @param {array} games
 * @returns 
 */
const getSurveyResult = (themeId, resultStepId, games) => {
	/* Get theme data */
	const themeData = themesData.find((t) => {return t.id === themeId;});

	/* Get survey step data */
	const resultStepData = (themeData && themeData.resultSteps 
		? themeData.resultSteps.find((step) => {return step.id === resultStepId;}) 
		: null
	);

	if (!resultStepData) return null;

	/* Get relevant game step data for this result page */
	const gameStepData = themeData.gameSteps.find((step) => {
		return resultStepData.gameStepId === step.id;
	});

	/* Get question */
	const question = gameStepData && gameStepData.questions
		? gameStepData.questions.find((question) => {return question.id === resultStepData.questionId;})
		: null
	;

	if (!question) return null;

	const surveyResult = {
		type: 'result', 
		questionId: question.id, 
		text: (resultStepData.text ? resultStepData.text : null), 
		options: [], 
		total: 0
	};

	/* Get data for all options of question */
	question.options.forEach((option) => {
		const optionObj = {optionId: option.id, text: (option.text ? option.text : null), value: 0};
		surveyResult.options.push(optionObj);
	});

	games.forEach((game) => {
		if (game.themeId === themeId) {
			const answers = game.surveyAnswers ? game.surveyAnswers : [];
			const questionAnswers = answers.filter((a) => {
				return (a.questionId === question.id);
			});
			questionAnswers.forEach((answer) => {
				if (answer.optionIds) {
					surveyResult.total += answer.optionIds.length;
					answer.optionIds.forEach((optionId) => {
						const resultOptionsIndex = surveyResult.options.findIndex((o) => {
							return o.optionId === optionId;
						});
						if (resultOptionsIndex >= 0) {
							surveyResult.options[resultOptionsIndex].value += 1;
						}
					});
				}
			});
		}
	});
	return surveyResult;
};


/**
 * Get question answers for all games of theme
 * @param {object} questionStep 
 * @param {array} groups 
 * @returns 
 */
const getQuestionAnswers = (questionStep, groups) => {
	if (!questionStep.questions || questionStep.questions.length === 0) return null;

	/* Get first question in step (there are only 1 per step anyway) */
	const question = questionStep.questions[0];
	const questionAnswers = {
		type: 'result', 
		questionId: questionStep.id, 
		text: (questionStep.text ? questionStep.text : null), 
		options: [], 
		total: 0
	};

	/* Get all answer options  */
	question.options.forEach((option) => {
		const optionObj = {optionId: option.id, text: (option.text ? option.text : null), value: 0};
		questionAnswers.options.push(optionObj);
	});

	groups.forEach((group) => {
		if (group.questions) {
			const answer = group.questions.find((q) => {
				return q.questionId === question.id;
			});
			if (answer) {
				questionAnswers.total += 1;
				const answerIndex = questionAnswers.options.findIndex((o) => {
					return o.optionId === answer.optionId;
				});
				if (answerIndex >= 0) {
					questionAnswers.options[answerIndex].value += 1;
				}
			}
		}
	});

	return questionAnswers;
};



export {
	getUniqueFacilitators,
	filterFacilitatorsGamesAndGroupsByDate,
	getGeneralStats,
	getSurveyResult,
	getQuestionAnswers
};